import React from 'react';
import { LeadActivity, LeadActivityType } from '../types';
import { formatDateTimeInTZ } from '../services/dateFormat';

interface TimelineProps {
  activities: LeadActivity[];
  timezone?: string;
}

const Timeline: React.FC<TimelineProps> = ({ activities, timezone }) => {
  // Fonction pour obtenir l'icône selon le type d'activité
  const getActivityIcon = (type: LeadActivityType) => {
    switch (type) {
      case LeadActivityType.CREATED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case LeadActivityType.STATUS_CHANGED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case LeadActivityType.ASSIGNED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case LeadActivityType.COMMENT_ADDED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case LeadActivityType.INFO_UPDATED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case LeadActivityType.APPOINTMENT_CREATED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case LeadActivityType.EMAIL_SENT:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case LeadActivityType.CALL_LOGGED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case LeadActivityType.FAVORITE_TOGGLED:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Fonction pour obtenir la couleur selon le type
  const getActivityColor = (type: LeadActivityType) => {
    switch (type) {
      case LeadActivityType.CREATED:
        return 'bg-green-500 text-white';
      case LeadActivityType.STATUS_CHANGED:
        return 'bg-blue-500 text-white';
      case LeadActivityType.ASSIGNED:
        return 'bg-purple-500 text-white';
      case LeadActivityType.COMMENT_ADDED:
        return 'bg-indigo-500 text-white';
      case LeadActivityType.INFO_UPDATED:
        return 'bg-amber-500 text-white';
      case LeadActivityType.APPOINTMENT_CREATED:
        return 'bg-pink-500 text-white';
      case LeadActivityType.EMAIL_SENT:
        return 'bg-cyan-500 text-white';
      case LeadActivityType.CALL_LOGGED:
        return 'bg-teal-500 text-white';
      case LeadActivityType.FAVORITE_TOGGLED:
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  // Décode les entités HTML héritées des anciennes activités (avant fix sanitizeText).
  const decodeHtmlEntities = (s: string): string => {
    if (!s || s.indexOf('&') === -1) return s;
    return s
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  };

  // Format absolu dans le fuseau horaire de l'entreprise.
  const formatDate = (dateString: string) => formatDateTimeInTZ(dateString, timezone);

  if (!activities || activities.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-slate-400 text-sm font-medium">Aucune activité enregistrée</p>
        <p className="text-slate-300 text-xs mt-1">Les actions sur ce prospect apparaîtront ici</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity, index) => (
        <div key={activity.id} className="flex gap-4 group">
          {/* Icône avec ligne verticale */}
          <div className="relative flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full ${getActivityColor(activity.type)} flex items-center justify-center shadow-lg z-10 transition-transform group-hover:scale-110`}>
              {getActivityIcon(activity.type)}
            </div>
            {index < activities.length - 1 && (
              <div className="orion-timeline-line w-0.5 h-full absolute top-10" />
            )}
          </div>

          {/* Contenu */}
          <div className="flex-1 pb-8">
            <div className="orion-modal-subcard rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-slate-800">
                    {decodeHtmlEntities(activity.description)}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-medium text-slate-500">
                      {activity.userName}
                    </span>
                    <span className="text-xs text-slate-300">•</span>
                    <span className="text-xs text-slate-400">
                      {formatDate(activity.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Métadonnées supplémentaires */}
              {activity.metadata && (activity.metadata.oldValue || activity.metadata.newValue) && (
                <div className="orion-timeline-meta mt-3 p-3 rounded-xl text-xs">
                  {activity.metadata.oldValue && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <span className="font-bold">Avant :</span>
                      <span className="line-through">{activity.metadata.oldValue}</span>
                    </div>
                  )}
                  {activity.metadata.newValue && (
                    <div className="flex items-center gap-2 text-slate-700 font-medium">
                      <span className="font-bold">Après :</span>
                      <span>{activity.metadata.newValue}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Timeline;
